import { Constants } from '@constants';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconEmailFilled,
    IconPhoneFilled,
    IconQuestionMarkFilled
} from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

type TNeedHelpVariant =
    'offers-desktop' | 'offers-mobile' | 'funding-desktop' | 'funding-mobile';

export const NeedHelpSection = ({ variant }: { variant: TNeedHelpVariant }) => {
    const isDark =
        variant === 'offers-desktop' || variant === 'funding-desktop';
    const isFunding =
        variant === 'funding-desktop' || variant === 'funding-mobile';
    const fontColor = isDark ? 'text-structure-100' : 'text-primary-10';
    const borderColor = isDark ? 'border-secondary-20' : 'border-structure-80';

    return (
        <>
            {variant !== 'funding-desktop' && (
                <Text
                    className={`block border-t ${borderColor} pt-xl font-bold ${fontColor}`}
                >
                    {isFunding ? (
                        <FormattedMessage
                            id="_.need_help.still_need_help"
                            defaultMessage="Still need help?"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.loan_summary.need_help"
                            defaultMessage="Need some help?"
                        />
                    )}
                </Text>
            )}

            <div className="flex flex-col gap-l pt-s">
                <div className="flex items-center gap-m">
                    <IconQuestionMarkFilled
                        className={`text-xl ${fontColor}`}
                    />
                    <Text className={`text-s ${fontColor}`}>
                        <FormattedMessage
                            id="_.need_help.faq"
                            defaultMessage="Find answers in our <a>FAQ</a>"
                            values={{
                                a: (chunks) => (
                                    <a
                                        href="/faq"
                                        className="font-bold underline"
                                    >
                                        {chunks}
                                    </a>
                                )
                            }}
                        />
                    </Text>
                </div>

                <div className="flex items-start gap-m">
                    <IconPhoneFilled
                        className={`mt-[2px] text-xl ${fontColor}`}
                    />
                    <div>
                        <Text className={`text-s ${fontColor}`}>
                            <FormattedMessage
                                id="_.need_help.call"
                                defaultMessage="Call us on <b>020 3778 0105</b>"
                                values={{
                                    b: (chunks) => (
                                        <span className="font-bold">
                                            {chunks}
                                        </span>
                                    )
                                }}
                            />
                        </Text>
                        <Text className={`text-s ${fontColor}`}>
                            <FormattedMessage
                                id="_.need_help.phone_hours"
                                defaultMessage="Monday to Friday (9am - 6pm)"
                            />
                        </Text>
                    </div>
                </div>

                <div className="flex items-center gap-m">
                    <IconEmailFilled className={`text-xl ${fontColor}`} />
                    <Text className={`text-s ${fontColor}`}>
                        <FormattedMessage
                            id="_.need_help.email"
                            defaultMessage="Email us at <a>{email}</a>"
                            values={{
                                email: Constants.EMAIL,
                                a: (chunks) => (
                                    <a
                                        href={`mailto:${Constants.EMAIL}`}
                                        className="font-bold underline"
                                    >
                                        {chunks}
                                    </a>
                                )
                            }}
                        />
                    </Text>
                </div>

                {!isFunding && (
                    <Text className={`text-s ${fontColor}`}>
                        <FormattedMessage
                            id="_.loan_summary.upload_docs_prompt"
                            defaultMessage="Been asked to upload additional docs? {link}"
                            values={{
                                link: (
                                    <Link
                                        to={maintainStateKey({
                                            pathname: '/trading'
                                        })}
                                        className="font-bold underline"
                                        onClick={() =>
                                            tracking.ampli.uploadDocumentsCtaClicked(
                                                {
                                                    suggested_product_status:
                                                        'approved'
                                                }
                                            )
                                        }
                                    >
                                        <FormattedMessage
                                            id="_.loan_summary.upload_docs_link"
                                            defaultMessage="Upload docs"
                                        />
                                    </Link>
                                )
                            }}
                        />
                    </Text>
                )}
            </div>
        </>
    );
};
